/**
 * Global header — draws the ornamental outline on (endpoint paths via DrawSVG,
 * connecting lines via scaleX), fades in the logo + links, wires the sound bars
 * to window.tamreAudio, and recolours the bars to each section's text colour as
 * the visitor scrolls.
 *
 * Plain script with `defer`; sits before the GSAP libs, so we wait via whenReady.
 * On the homepage the outline draws on `tamre:enter` (dispatched by
 * intro-portal.js); on other pages it draws on load. Respects
 * prefers-reduced-motion. Re-reveals on Shopify editor section reloads.
 */
(function () {
  var bound = false;

  function ready() {
    return window.gsap && window.DrawSVGPlugin;
  }
  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function setupHeader(forceReveal) {
    gsap.registerPlugin(DrawSVGPlugin);

    var header = document.querySelector('[data-header]');
    if (!header) return;

    var outline = header.querySelector('.header__outline');
    var draws = header.querySelectorAll('.header__draw'); // endpoint paths
    var topLine = header.querySelector('[data-header-line-top]');
    var botLine = header.querySelector('[data-header-line-bottom]');
    var lines = [topLine, botLine].filter(Boolean);
    var logo = header.querySelector('[data-header-logo]');
    var items = header.querySelectorAll('[data-header-item]');
    var soundBtn = document.querySelector('[data-sound-toggle]');

    // Logo → home. On the homepage (past the intro) smooth-scroll to top with no
    // reload, so ambient audio keeps playing. From any other page, flag the intro
    // to skip on arrival so we land on the post-intro content.
    if (logo && logo.tagName === 'A' && !logo.__navWired) {
      logo.__navWired = true;
      logo.addEventListener('click', function (e) {
        if (document.querySelector('[data-intro-portal]')) {
          e.preventDefault();
          if (window.lenis) window.lenis.scrollTo(0);
          else window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          try { sessionStorage.setItem('tamre:skip-intro', '1'); } catch (err) {}
        }
      });
    }

    // "Static mode": no draw-on reveal. True under reduced-motion AND inside
    // the Shopify theme editor (Shopify.designMode), so the header shows at rest.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);
    var isHome = !!document.querySelector('[data-intro-portal]');

    /* ---- Sound bars: show + wire to the global audio controller. ----- */
    function wireSound() {
      if (!soundBtn || soundBtn.__wired) return;
      if (!window.tamreAudio) {
        soundBtn.hidden = true;
        return;
      }
      soundBtn.__wired = true;
      soundBtn.hidden = false;
      soundBtn.addEventListener('click', function () {
        window.tamreAudio.toggle();
      });
      window.tamreAudio.onChange(function (playing) {
        soundBtn.classList.toggle('is-playing', playing);
        soundBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      });
    }
    if (window.tamreAudio) wireSound();
    else document.addEventListener('tamre:audio-ready', wireSound, { once: true });

    /* ---- Recolour the bars to the section currently under them. ------ */
    function updateBarColor() {
      if (!soundBtn || soundBtn.hidden) return;
      var r = soundBtn.getBoundingClientRect();
      var y = r.top + r.height / 2;
      var color = '';
      var hide = false;
      document.querySelectorAll('[data-ui-color]').forEach(function (s) {
        var sr = s.getBoundingClientRect();
        if (sr.top <= y && sr.bottom > y) {
          color = s.getAttribute('data-ui-color');
          hide = s.hasAttribute('data-ui-hide-bars');
        }
      });
      soundBtn.style.color = color;
      // Some sections (e.g. the footer) place their own content where the bars
      // sit — hide the bars while one of those is under them.
      soundBtn.classList.toggle('sound-bars--hidden', hide);
    }

    /* ---- Reveal: draw the outline + fade in logo / links. ------------ */
    function reveal() {
      if (header.__revealed) return;
      header.__revealed = true;
      updateBarColor();

      if (staticMode) return; // CSS shows everything at rest

      gsap.set(outline, { opacity: 1 });
      gsap.set(draws, { drawSVG: 0 });
      gsap.set(lines, { scaleX: 0 });

      var tl = gsap.timeline({ defaults: { ease: 'power1.inOut' } });
      tl.to(draws, { drawSVG: '100%', duration: 1.2, stagger: 0.15 }, 0);
      tl.to(lines, { scaleX: 1, duration: 1.2 }, 0.2);
      tl.to(logo, { opacity: 1, duration: 0.8 }, 1.0);
      if (items.length) tl.to(items, { opacity: 1, duration: 0.8, stagger: 0.1 }, 1.1);
      if (soundBtn && !soundBtn.hidden) {
        tl.to(soundBtn, { opacity: 1, duration: 0.8 }, 1.2);
      }
    }

    if (forceReveal) {
      header.__revealed = false;
      reveal();
    } else if (isHome && !staticMode) {
      // Homepage: the intro dispatches `tamre:enter` on entry to draw us on.
      // In static mode that click never happens, so reveal on load instead.
      document.addEventListener('tamre:enter', reveal, { once: true });
    } else {
      reveal();
    }

    /* ---- Scroll / resize: keep the bar colour in sync. --------------- */
    if (!bound) {
      bound = true;
      window.addEventListener('scroll', updateBarColor, { passive: true });
      window.addEventListener('resize', updateBarColor);
    }
  }

  whenReady(function () {
    setupHeader(false);
  });

  // Shopify editor: re-reveal immediately when the header section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (e.target && e.target.querySelector && e.target.querySelector('[data-header]')) {
      setupHeader(true);
    }
  });
})();
